import type {DraftKey, PositionSide, SignalTradeDraft} from '../types';
import {normalizePrice} from './priceNormalization';

export function createTradeDrafts(
    signalDirection: 1 | -1 | 0,
    confidence: number | undefined,
    entryPrice: number,
    targetPrice: number,
    stopLossPrice: number,
): Partial<Record<DraftKey, SignalTradeDraft>> {
    const common = {orderType: 'limit' as const, entryPrice, allocationPercent: 5, leverage: 3, manuallyEdited: false};
    if (0 === signalDirection) {
        return {
            MANUAL_LONG: {...common, tradeIntent: 'MANUAL', posSide: 'long', targetPrice, stopLossPrice},
            MANUAL_SHORT: {
                ...common,
                tradeIntent: 'MANUAL',
                posSide: 'short',
                targetPrice: stopLossPrice,
                stopLossPrice: targetPrice
            },
        };
    }
    const forwardSide: PositionSide = 1 === signalDirection ? 'long' : 'short';
    const reverseSide: PositionSide = 'long' === forwardSide ? 'short' : 'long';
    return {
        FORWARD: {
            ...common,
            tradeIntent: 'FORWARD',
            posSide: forwardSide,
            targetPrice,
            stopLossPrice,
            forwardConfidence: confidence
        },
        REVERSE: {
            ...common,
            tradeIntent: 'REVERSE',
            posSide: reverseSide,
            targetPrice: stopLossPrice,
            stopLossPrice: targetPrice,
            forwardConfidence: confidence
        },
    };
}

export function intervalToMillis(interval: string): number {
    const match = /^(\d+)([mMhHdD])$/.exec(interval.trim());
    if (!match) return 3_600_000;
    const amount = Number(match[1]);
    const unit = match[2].toLowerCase();
    if ('m' === unit) return amount * 60_000;
    if ('h' === unit) return amount * 3_600_000;
    return amount * 86_400_000;
}

export function validateTradePlanPrices(draft: SignalTradeDraft): string | null {
    const {entryPrice, targetPrice, stopLossPrice, posSide} = draft;
    if (![entryPrice, targetPrice, stopLossPrice].every(value => Number.isFinite(value) && 0 < value)) {
        return '入场价、目标位和止损位必须为有效正数';
    }
    if ('long' === posSide && !(targetPrice > entryPrice && entryPrice > stopLossPrice)) {
        return '开多需满足：目标位 > 入场位 > 止损位';
    }
    if ('short' === posSide && !(targetPrice < entryPrice && entryPrice < stopLossPrice)) {
        return '开空需满足：目标位 < 入场位 < 止损位';
    }
    return null;
}

export function normalizeTradeDraftPrices(
    draft: SignalTradeDraft,
    tickSz: number,
): SignalTradeDraft {
    return {
        ...draft,
        entryPrice: normalizePrice(draft.entryPrice, tickSz),
        targetPrice: normalizePrice(draft.targetPrice, tickSz),
        stopLossPrice: normalizePrice(draft.stopLossPrice, tickSz),
    };
}

export function calculateProfitLossRatio(entryPrice: number, targetPrice: number, stopLossPrice: number): number {
    const reward = Math.abs(targetPrice - entryPrice);
    const risk = Math.abs(entryPrice - stopLossPrice);
    if (![reward, risk].every(Number.isFinite) || 0 >= reward || 0 >= risk) return 0;
    return Math.round(reward / risk * 100) / 100;
}
